import React, { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import ShimmerButton from '../components/magicui/shimmer-button';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const Contact = () => {
    const [, setIsMobile] = useState(false);
    const navigate = useNavigate();

    const handleInvalid = (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        (e.target as HTMLInputElement).setCustomValidity("Champ obligatoire");
    };

    const handleInput = (e: FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        (e.target as HTMLInputElement).setCustomValidity("");
    };

    const [form, setForm] = useState({
        name: "",
        prenom: "",
        email: "",
        phone: "",
        message: "",
    });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            const isMobile = width < 768;
            setIsMobile(isMobile);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            const response = await fetch(' https://fbconseil-c706cd2b1837.herokuapp.com/api/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    firstName: form.prenom,
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    message: form.message,
                }),
            });

            const result = await response.json();

            if (result.success) {
                setForm({
                    name: "",
                    prenom: "",
                    email: "",
                    phone: "",
                    message: "",
                });

                confirmAlert({
                    title: 'Merci pour votre message',
                    message: 'Nous reviendrons vers vous dès que possible.',
                    buttons: [
                        {
                            label: 'Ok',
                            onClick: () => {
                                navigate('/');
                            }
                        }
                    ]
                });
            } else {
                console.error('Failed to send email:', result.message);
                alert("Ah, quelque chose s'est mal passé. Veuillez réessayer.");
            }
        } catch (error) {
            console.error('Error:', error);
            alert("Ah, quelque chose s'est mal passé. Veuillez réessayer.");
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm({
            ...form,
            [e.target.name]: e.target.value
        });
    };

    const inputClasses = "w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-[#45B8A9] focus:ring-2 focus:ring-[rgba(69,184,169,0.2)] transition-all duration-200 bg-gray-50/50 text-gray-700";

    return (
        <>
        <Helmet>
            <title>Contact - FB Conseils | Expert en création d'entreprise</title>
            <meta name="description" content="Contactez FB Conseils pour un accompagnement personnalisé dans la création de votre entreprise. Conseils en forme juridique et budget prévisionnel." />
            <link rel="canonical" href="https://maformejuridique.fr/contact" />
            <meta property="og:title" content="Contactez FB Conseils" />
            <meta property="og:description" content="Accompagnement personnalisé pour la création de votre entreprise." />
            <meta property="og:url" content="https://maformejuridique.fr/contact" />
        </Helmet>
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'linear-gradient(180deg, #f8fffe 0%, #eef7f5 40%, #f0f9f7 100%)' }}>
            <div className="max-w-6xl mx-auto mt-24">
                {/* Page header */}
                <motion.div
                    className="text-center mb-16"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <span
                        className="text-sm tracking-[0.25em] uppercase font-medium"
                        style={{ color: '#45B8A9', fontFamily: "'Outfit', sans-serif" }}
                    >
                        Nous contacter
                    </span>
                    <h1
                        className="text-4xl md:text-5xl mt-4"
                        style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                    >
                        Contactez-nous
                    </h1>
                    <div className="w-16 h-0.5 mx-auto mt-6" style={{ backgroundColor: '#45B8A9' }} />
                </motion.div>

                <div className="grid lg:grid-cols-2 gap-12 items-start">
                    {/* Formulaire */}
                    <motion.div
                        className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-500 p-8 lg:p-10 border border-gray-100"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                        Nom<span className="ml-1" style={{ color: '#45B8A9' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        required
                                        className={inputClasses}
                                        style={{ fontFamily: "'Outfit', sans-serif" }}
                                        placeholder="Votre nom"
                                        onInvalid={handleInvalid}
                                        onInput={handleInput}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                        Prénom<span className="ml-1" style={{ color: '#45B8A9' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="prenom"
                                        required
                                        className={inputClasses}
                                        style={{ fontFamily: "'Outfit', sans-serif" }}
                                        placeholder="Votre prénom"
                                        onInvalid={handleInvalid}
                                        onInput={handleInput}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                        Email<span className="ml-1" style={{ color: '#45B8A9' }}>*</span>
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        required
                                        className={inputClasses}
                                        style={{ fontFamily: "'Outfit', sans-serif" }}
                                        placeholder="vous@exemple.com"
                                        onInvalid={handleInvalid}
                                        onInput={handleInput}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                        Téléphone
                                    </label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        className={inputClasses}
                                        style={{ fontFamily: "'Outfit', sans-serif" }}
                                        placeholder="Votre numéro"
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                                    Message<span className="ml-1" style={{ color: '#45B8A9' }}>*</span>
                                </label>
                                <textarea
                                    name="message"
                                    required
                                    rows={6}
                                    className={`${inputClasses} resize-none`}
                                    style={{ fontFamily: "'Outfit', sans-serif" }}
                                    placeholder="Votre message..."
                                    onInvalid={handleInvalid}
                                    onInput={handleInput}
                                    onChange={handleInputChange}
                                />
                            </div>

                            <div className="flex justify-center pt-2">
                                <ShimmerButton
                                    background="#003942"
                                    shimmerColor="#45B8A9"
                                >
                                    <span style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600 }}>
                                        Envoyer votre message
                                    </span>
                                </ShimmerButton>
                            </div>
                        </form>
                    </motion.div>

                    {/* Section À propos */}
                    <motion.div
                        className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow duration-500 p-8 lg:p-10 border border-gray-100"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.35 }}
                    >
                        <h2
                            className="text-2xl mb-6"
                            style={{ color: '#003942', fontFamily: "'Cormorant', serif", fontWeight: 700 }}
                        >
                            À propos de nous
                        </h2>

                        <div className="relative group mb-8 flex justify-center">
                            <img
                                src='/img/fnou.jpeg'
                                alt="Fannie"
                                className="max-h-[380px] w-auto transition-all duration-300 group-hover:brightness-105"
                                style={{ filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.1))' }}
                            />
                        </div>

                        <div className="space-y-4 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }}>
                            <p className="text-gray-600 text-sm md:text-base">
                                Nous sommes deux experts en conseil pour la création d'entreprise, avec plus de 10 années d'expérience en cabinet d'expertise comptable.
                            </p>
                            <p className="text-gray-600 text-sm md:text-base">
                                Notre mission est de vous accompagner dans le choix crucial de votre forme juridique, étape fondamentale pour bien démarrer votre aventure entrepreneuriale.
                            </p>
                            <p className="text-gray-600 text-sm md:text-base">
                                C'est dans cet esprit que nous avons créé maformejuridique.fr, pour mettre notre expertise à votre service.
                            </p>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
        </>
    );
};

export default Contact;
